import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import Switch from '@mui/material/Switch';
import React, { useRef, useState } from 'react';
import Spinner from '../../../components/spinner';
import Errors from '../../errors';
import MuiAlert from '@mui/material/Alert';
import { Link } from 'react-router-dom';
import Pagination from '../../../components/elements/pagination';
import DeleteModal from '../../../components/elements/deleteModal';
import useProductTable from '../../../hooks/product/useProductTable';
import useProductActions from '../../../hooks/product/useProductActions';
import useCategoryManagement from '../../../hooks/product/useCategoryManagement';
import useProductRearrange from '../../../hooks/product/useProductRearrange';
import Modal from "@mui/material/Modal";
import { GrAdd } from "react-icons/gr";
import { IoArrowBack, IoCloseCircle, IoDocumentText } from "react-icons/io5";
import { MdCategory } from "react-icons/md";
import Loader from "../../../components/loader";
import useSerialUpload from "../../../hooks/file/useSerialUpload";
import useCreate from "../../../hooks/useCreate";
import toast from "react-hot-toast";
import CategoryManagement from '../../../components/product/CategoryManagement';
import ProductRearrangementModal from '../../../components/product/ProductRearrangementModal';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Products() {
    const {
        data,
        paramsObject,
        searchValue,
        handleSearchVal,
        handlePageClick,
        handleSetLimit
    } = useProductTable();

    const {
        productId,
        isDeleteModalOpen,
        handleDelete,
        closeDeleteModal,
        confirmDelete,
        selectedProduct,
        productDocuments,
        isDocumentModalOpen,
        handleOpenDocumentModal,
        handleCloseDocumentModal,
        handleRemoveDocument,
        handlePositionChange,
        handleToggleVisibility,
        handleStatusToggle,
        isTogglingStatus
    } = useProductActions(data);

    // Category management hook
    const {
        isCategoryModalOpen,
        handleOpenCategoryModal,
        handleCloseCategoryModal,
        selectedProduct: selectedProductForCategory,
        allCategories,
        mappedCategories,
        categoriesLoading,
        isAddingCategory,
        isRemovingCategory,
        isCategoryMapped,
        getMappedCategory,
        handleAddCategory,
        handleRemoveCategory,
        handleSearchCategories,
        searchQuery
    } = useCategoryManagement(data);

    // Product rearrangement hook
    const {
        isRearrangeModalOpen,
        products: sortableProducts,
        isLoading: isLoadingProducts,
        isSaving: isSavingOrder,
        handleOpenRearrangeModal,
        handleCloseRearrangeModal,
        handleOrderChange,
        handleSaveOrder
    } = useProductRearrange();

    // Document upload refs and states
    const documentInputRef = useRef(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Document upload handler with serial upload hook
    const { upload: serialDocumentUpload, isUploading: isSerialUploading, progress } = useSerialUpload({
        url: "upload-document",
        onSuccess: (results) => {
            toast.success(`${results.length} files uploaded successfully`, {
                position: "top-right",
                duration: 3000,
            });
            setUploadProgress(0);
            // Refresh data to get updated documents
            data.refetch();
        },
        onError: (err) => {
            toast.error(`Error during file uploads: ${err?.message || 'Unknown error'}`, {
                position: "top-right",
                duration: 3000,
            });
            setUploadProgress(0);
        },
        onFileSuccess: (result, index, file) => {
            // Create document mapping with the uploaded document
            if (result?.document_id && selectedProduct?.product_id) {
                // Calculate a unique position for each document in the batch
                // Use the current productDocuments length + index + 1 to ensure unique positions
                const batchStartPosition = (productDocuments?.length || 0) + 1;
                const newPosition = batchStartPosition + index;

                // Determine if the file is a video
                const isVideo = file.type.startsWith('video/');

                createDocumentMap.mutate({
                    product_id: selectedProduct.product_id,
                    document_id: result.document_id,
                    position: newPosition, // Each file gets incremented position
                    file_type: isVideo ? 'video' : 'image' // Add file type information if API supports it
                });
            }
        },
        onFileError: (err, index, file) => {
            const fileType = file.type.startsWith('video/') ? 'video' : 'image';
            toast.error(`Failed to upload ${fileType} ${file.name}: ${err?.message || 'Unknown error'}`, {
                position: "top-right",
                duration: 3000,
            });
        }
    });

    // Create document mapping
    const { setDataToServer: createDocumentMap } = useCreate({
        url: "create-product-document-map",
        refreshUrl: "get-product-list",
        onSuccess: () => {
        },
        onError: (err) => {
            toast.error(`Error adding document: ${err?.message || 'Unknown error'}`, {
                position: "top-right",
                duration: 3000,
            });
        },
    });

    // Open file dialog for document upload
    const handleClickOpenFileDialog = () => {
        documentInputRef.current.click();
    };

    // Handle multiple document uploads using serial upload hook
    const handleDocumentUpload = (e) => {
        const files = e.target?.files;
        if (!files || !files.length) return;

        // Show a toast notification that uploads are starting
        toast.loading(`Uploading ${files.length} file(s)...`, {
            id: 'document-upload',
            duration: Infinity
        });

        // Use our serial upload hook
        serialDocumentUpload.mutate({
            files: Array.from(files), // Convert FileList to array
            type: 'products'
        }, {
            onSettled: () => {
                toast.dismiss('document-upload');
                e.target.value = null;
            }
        });

        // Track progress
        setUploadProgress(progress);
    };

    // Determine if a document is a video based on its path or extension
    const isVideoFile = (path) => {
        if (!path) return false;
        const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.quicktime'];
        return videoExtensions.some(ext => path.toLowerCase().endsWith(ext));
    };
    
    // Prevent scroll wheel from changing number input values
    const handlePositionWheel = (e) => {
        // Prevent the default wheel behavior on number inputs
        e.preventDefault();
        e.stopPropagation();
        // Ensure the input doesn't lose focus on wheel events
        e.target.blur();
        e.target.focus();
    };

    return (
        <>
            <div className='category px-4'>
                <div className='category__head headingBorder__b'>
                    <h2 className='titleHeading'>Projects</h2>
                </div>
                <div className='category_table py-5 space-y-5'>
                    <div className='flex items-end space-x-4'>
                        <div className='flex-1'>
                            <input
                                onChange={handleSearchVal}
                                value={searchValue}
                                type="text"
                                placeholder='Search By Name'
                                className='input'
                            />
                        </div>
                        <div>
                            <Link to={`add-product`} className='block'>
                                <Button variant="outlined" startIcon={<AddIcon />} className='primaryBtn-outlined'>
                                    Add Project
                                </Button>
                            </Link>
                        </div>
                        <div>
                            <Button 
                                variant="outlined" 
                                startIcon={<SwapVertIcon />} 
                                className='primaryBtn-outlined'
                                onClick={handleOpenRearrangeModal}
                            >
                                Rearrange Projects
                            </Button>
                        </div>
                        <div>
                            <label htmlFor="" className='label'>Limit</label>
                            <select name="limit" value={paramsObject.limit} className='input' onChange={handleSetLimit}>
                                {Array(5).fill(1).map((_, idx) => {
                                    return <option key={idx} value={(idx + 1) * 10}>{(idx + 1) * 10}</option>
                                })}
                            </select>
                        </div>
                    </div>
                    {data?.isLoading || data?.isFetching
                        ? <Spinner />
                        : data?.status === "error"
                            ? <Errors errorObject={data?.error} inline />
                            : data?.data?.data?.length === 0
                                ? <div className="bg-white rounded-lg shadow p-8 text-center">
                                    <p className="text-lg text-gray-600">No projects found</p>
                                </div>
                                : <div className="overflow-x-auto bg-white rounded-lg shadow overflow-y-auto relative">
                                    <table className="border-collapse table-auto w-full whitespace-no-wrap bg-white table-striped relative">
                                        <thead>
                                            <tr className="text-left">
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs w-16">
                                                    ID
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center w-40">
                                                    Banner
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center w-40">
                                                    Thumbnail
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs w-1/4">
                                                    Name
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs w-1/3">
                                                    Title
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center w-32">
                                                    Status
                                                </th>
                                                <th className="bg-gray-100 sticky top-0 border-b border-gray-200 px-6 py-2 text-gray-600 font-bold tracking-wider uppercase text-xs text-center w-40">
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.data?.data?.map((product, idx) => {
                                                const { product_id, name, title, banner, thumbnail, active, product_documents, product_categories } = product
                                                return <tr key={idx} >
                                                    <td className="border-dashed border-t border-gray-200 px-6 py-3">
                                                        <span className="text-gray-700 flex items-center text-sm">
                                                            {paramsObject.limit * +paramsObject.page_number + idx + 1}
                                                        </span>
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 text-center">
                                                        <div className='w-20 h-20 mx-auto p-2'>
                                                            <img
                                                                src={banner || "https://placehold.co/200x200?text=No+Banner"}
                                                                alt={name}
                                                                className='w-full h-full object-contain'
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 text-center">
                                                        <div className='w-20 h-20 mx-auto p-2'>
                                                            <img
                                                                src={thumbnail || "https://placehold.co/200x200?text=No+Thumbnail"}
                                                                alt={`${name} thumbnail`}
                                                                className='w-full h-full object-contain'
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 px-6 py-3">
                                                        <span className="text-gray-700 flex items-center text-sm">
                                                            {name}
                                                        </span>
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 px-6 py-3">
                                                        <span className="text-gray-700 flex items-center text-sm">
                                                            {title}
                                                        </span>
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 px-6 py-3 text-center">
                                                        <Switch
                                                            checked={active === "1"}
                                                            onChange={() => handleStatusToggle(product_id, active)}
                                                            color="primary"
                                                            size="small"
                                                            disabled={isTogglingStatus}
                                                            title={active === "1" ? "Active" : "Inactive"}
                                                        />
                                                    </td>
                                                    <td className="border-dashed border-t border-gray-200 px-4 py-3">
                                                        <div className='flex items-center justify-center space-x-2'>
                                                            {/* Document button - shows document count badge if available */}
                                                            <div onClick={() => handleOpenDocumentModal(product)}>
                                                                <IconButton
                                                                    size="small"
                                                                    className="relative"
                                                                    title="Manage Documents"
                                                                >
                                                                    <IoDocumentText />
                                                                    {product_documents && product_documents.length > 0 && (
                                                                        <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                                            {product_documents.length}
                                                                        </span>
                                                                    )}
                                                                </IconButton>
                                                            </div>

                                                            {/* Category button - add category management icon */}
                                                            <div onClick={() => handleOpenCategoryModal(product)}>
                                                                <IconButton
                                                                    size="small"
                                                                    className="relative"
                                                                    title="Manage Categories"
                                                                >
                                                                    <MdCategory />
                                                                    {product_categories && product_categories.length > 0 && (
                                                                        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                                            {product_categories.length}
                                                                        </span>
                                                                    )}
                                                                </IconButton>
                                                            </div>

                                                            <Link to={`edit-product/${product_id}`}>
                                                                <IconButton size="small">
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Link>
                                                            <div onClick={() => handleDelete(product_id)}>
                                                                <IconButton size="small">
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            })}
                                        </tbody>
                                    </table>
                                </div>}
                    <div>
                        <Pagination
                            currentPage={+paramsObject.page_number}
                            lengthofItems={data?.data?.total_count}
                            limit={paramsObject.limit}
                            onPageChange={handlePageClick}
                        />
                    </div>
                </div>
            </div>

            {/* Delete Product Modal */}
            <DeleteModal
                open={isDeleteModalOpen}
                handleClose={closeDeleteModal}
                title={'Product Delete'}
                message={'Are you sure you want to delete this product?'}
                onDelete={confirmDelete}
            />

            {/* Document Management Modal */}
            <Modal
                open={isDocumentModalOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDocumentModal();
                    }
                }}
                disableEscapeKeyDown={true}
                aria-labelledby="document-modal-title"
                aria-describedby="document-modal-description"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <div className="w-full sm:w-11/12 md:w-10/12 lg:w-9/12 xl:w-8/12 max-w-5xl mx-auto bg-white rounded-md flex flex-col max-h-[90vh]">
                    <div className="head-modal py-3 px-4 sm:px-6 border-b">
                        <div className="flex items-center space-x-4">
                            <div
                                className="w-7 h-7 cursor-pointer"
                                onClick={handleCloseDocumentModal}
                            >
                                <IoArrowBack className="w-full h-full" />
                            </div>
                            <h2 className="heading text-xl sm:text-2xl truncate">
                                {selectedProduct?.name ? `Manage Media for '${selectedProduct.name}'` : 'Manage Media'}
                            </h2>
                        </div>
                    </div>

                    {/* Make this div scrollable */}
                    <div className="body-modal py-4 px-4 sm:px-6 flex-1 overflow-y-auto">
                        {/* Modal-specific loader for document operations */}
                        {(isSerialUploading || createDocumentMap.isLoading) && (
                            <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex flex-col items-center justify-center">
                                <Loader className={"text-black !w-12 !h-12"} />
                                {isSerialUploading && progress > 0 && (
                                    <div className="mt-4 w-64">
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${progress}%` }}></div>
                                        </div>
                                        <p className="text-sm text-center mt-2">Uploading: {progress}%</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Document grid - responsive columns based on screen size */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                            {productDocuments?.length > 0 ? (
                                productDocuments.map((doc, idx) => (
                                    <div key={doc.product_document_map_id || idx}>
                                        <div className="w-full overflow-hidden border p-2 relative">
                                            <div className="w-6 h-6 cursor-pointer float-right z-10">
                                                <IoCloseCircle
                                                    className="w-full h-full"
                                                    onClick={() => handleRemoveDocument(doc.product_document_map_id)}
                                                />
                                            </div>
                                            {isVideoFile(doc.path) ? (
                                                <video
                                                    src={doc.path}
                                                    className="w-full h-32 sm:h-40 object-contain"
                                                    controls
                                                    preload="metadata"
                                                    controlsList="nodownload"
                                                    playsInline
                                                    poster={doc.thumbnail_path || ''}
                                                />
                                            ) : (
                                                <img
                                                    src={doc.path}
                                                    alt={doc.name || `Document ${idx + 1}`}
                                                    className="w-full h-32 sm:h-40 object-contain"
                                                />
                                            )}
                                            <div className="my-2">
                                                <p className="text-sm font-medium truncate">
                                                    {doc.name || `Document ${idx + 1}`}
                                                </p>
                                                <span className="text-xs text-gray-500 mt-1 inline-block">
                                                    {isVideoFile(doc.path) ? 'Video' : 'Image'}
                                                </span>

                                                {/* Improved controls layout */}
                                                <div className="mt-2 border-t pt-2">
                                                    {/* Position control in its own row */}
                                                    <div className="flex items-center justify-between mb-1">
                                                        <label className="text-xs text-gray-500">Position:</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            defaultValue={doc.position}
                                                            className="w-16 p-1 text-xs border border-gray-300 rounded ml-2"
                                                            onBlur={(e) => handlePositionChange(e, doc)}
                                                            onWheel={handlePositionWheel}
                                                            onMouseEnter={(e) => e.target.addEventListener('mousewheel', handlePositionWheel, { passive: false })}
                                                            onMouseLeave={(e) => e.target.removeEventListener('mousewheel', handlePositionWheel)}
                                                            step="1"
                                                        />
                                                    </div>

                                                    {/* Visibility toggle */}
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs text-gray-500">Visibility:</span>
                                                        <div className="flex items-center">
                                                            <span className="text-xs mr-1">
                                                                {doc.active === "1" ? "Visible" : "Hidden"}
                                                            </span>
                                                            <Switch
                                                                size="small"
                                                                checked={doc.active === "1"}
                                                                onChange={() => handleToggleVisibility(
                                                                    doc.product_document_map_id,
                                                                    doc.active === "1"
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4 text-center py-10">
                                    <p className="text-gray-500">No images or videos found for this project</p>
                                </div>
                            )}

                            {/* Upload button */}
                            <div
                                onClick={handleClickOpenFileDialog}
                                className="w-full h-36 sm:h-48 cursor-pointer select-none border-2 rounded-md border-dashed border-gray-400 flex flex-col items-center justify-center hover:shadow-lg"
                            >
                                <GrAdd className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400" />
                                <small>Add Images/Videos</small>
                            </div>
                        </div>

                        <input
                            type="file"
                            className="hidden"
                            ref={documentInputRef}
                            onChange={handleDocumentUpload}
                            multiple
                            accept="image/jpeg, image/png, image/jpg, video/mp4, video/mov, video/quicktime, video/ogg, video/webm"
                        />
                    </div>

                    <div className="foot-modal py-4 text-center border-t">
                        <Button
                            variant="contained"
                            className="primaryBtn-contained px-8 sm:px-10"
                            onClick={handleCloseDocumentModal}
                            disabled={isSerialUploading || createDocumentMap.isLoading}
                        >
                            Done
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Category Management Modal */}
            <CategoryManagement
                open={isCategoryModalOpen}
                onClose={handleCloseCategoryModal}
                selectedProduct={selectedProductForCategory}
                allCategories={allCategories}
                mappedCategories={mappedCategories}
                categoriesLoading={categoriesLoading}
                isAddingCategory={isAddingCategory}
                isRemovingCategory={isRemovingCategory}
                isCategoryMapped={isCategoryMapped}
                getMappedCategory={getMappedCategory}
                handleAddCategory={handleAddCategory}
                handleRemoveCategory={handleRemoveCategory}
                handleSearchCategories={handleSearchCategories}
                searchQuery={searchQuery}
            />

            {/* Product Rearrangement Modal */}
            <ProductRearrangementModal
                open={isRearrangeModalOpen}
                onClose={handleCloseRearrangeModal}
                products={sortableProducts}
                isLoading={isLoadingProducts}
                isSaving={isSavingOrder}
                onSave={handleSaveOrder}
                onOrderChange={handleOrderChange}
            />
        </>
    );
}

export default Products;